import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ShowCard from '../components/ShowCard';
import { BedtimeIndicator } from '../components/BedtimeMode';
import { Tv, Zap, ChevronRight, Sparkles, Radio, Play, ListMusic, Clock, Star, Heart, Coffee } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { key: 'cartoon', label: 'Cartoons', icon: Sparkles, color: '#FF3860', desc: 'Classic animated favorites' },
  { key: 'live_action', label: 'Live Action', icon: Tv, color: '#4AC6FF', desc: 'Real-life kids shows' },
];

export default function HomePage() {
  const { user } = useAuth();
  const [shows, setShows] = useState([]);
  const [newShows, setNewShows] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [featuredShow, setFeaturedShow] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/shows`),
      axios.get(`${API}/shows/new?limit=5`),
      axios.get(`${API}/playlists`),
      axios.get(`${API}/featured-show`),
    ]).then(([showsRes, newRes, playlistsRes, featuredRes]) => {
      setShows(showsRes.data);
      setNewShows(newRes.data);
      setPlaylists(playlistsRes.data);
      setFeaturedShow(featuredRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/watch-history/continue`, { withCredentials: true })
        .then(r => setContinueWatching(r.data))
        .catch(() => {});
    }
  }, [user]);

  const featured = shows.slice(0, 4);

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#FDFBEC]" data-testid="hero-section">
        <div className="pattern-bg" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-16 md:py-24 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF3860]/10 border-2 border-[#FF3860] mb-6">
                <Radio size={14} strokeWidth={2.5} className="text-[#FF3860]" />
                <span className="text-sm font-bold text-[#FF3860] font-['IBM_Plex_Mono'] uppercase tracking-wider">Now Broadcasting</span>
              </div>
              <h1 className="font-['Fredoka'] text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0A0A] tracking-tight leading-none mb-6">
                Your Favorite<br />
                <span className="text-[#FF3860]">Cartoons</span> From Times Past
              </h1>
              <p className="text-base md:text-lg text-[#4B5563] font-['Nunito'] font-medium leading-relaxed mb-8 max-w-lg">
                Relive the magic of 90s and early 2000s kids TV. Calm, gentle-paced shows your family will love — handpicked and available via YouTube.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/browse"
                  className="neo-btn inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#FF3860] text-white text-base"
                  data-testid="hero-browse-btn"
                >
                  <Tv size={18} strokeWidth={2.5} /> Browse Shows
                </Link>
                <Link
                  to="/tv-schedule"
                  className="neo-btn inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#FFDE00] text-[#0A0A0A] text-base"
                  data-testid="hero-tv-mode-btn"
                >
                  <Zap size={18} strokeWidth={2.5} /> TV Mode
                </Link>
              </div>
            </div>
            <div className="flex-1 flex justify-center">
              <div className="animate-float">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/ebea1ea5-9731-4354-90db-f3840d8f2692/images/2d1583aaab3949d96819d7dd04ab605a8cf09d02b452147d06a872a3b74fcbb7.png"
                  alt="Vintage CRT Television"
                  className="w-72 md:w-96 drop-shadow-2xl"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Featured Show */}
      {featuredShow && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pt-16" data-testid="weekly-featured">
          <div className="neo-card overflow-hidden bg-[#FF3860]/5 border-[#FF3860]">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-72 shrink-0 border-b-2 md:border-b-0 md:border-r-2 border-[#0A0A0A] overflow-hidden">
                <img
                  src={featuredShow.thumbnail_url}
                  alt={featuredShow.title}
                  className="w-full h-48 md:h-full object-cover"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <Star size={16} strokeWidth={2.5} fill="#FFDE00" className="text-[#FFDE00]" />
                  <span className="text-xs font-bold text-[#FF3860] font-['IBM_Plex_Mono'] uppercase tracking-wider">Show of the Week</span>
                </div>
                <h3 className="font-['Fredoka'] text-2xl font-black text-[#0A0A0A] mb-2">{featuredShow.title}</h3>
                <p className="text-sm text-[#4B5563] font-['Nunito'] line-clamp-2 mb-4">{featuredShow.description}</p>
                <Link
                  to={`/show/${featuredShow.id}`}
                  className="neo-btn inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#FF3860] text-white text-sm self-start"
                  data-testid="watch-featured-btn"
                >
                  <Play size={16} strokeWidth={2.5} fill="white" /> Explore This Show
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Bedtime Indicator */}
      {user && (
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pt-6">
          <BedtimeIndicator />
        </div>
      )}

      {/* New This Week */}
      {newShows.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pt-16" data-testid="new-this-week-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFDE00] border-2 border-[#0A0A0A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
                <Sparkles size={20} strokeWidth={2.5} className="text-[#0A0A0A]" />
              </div>
              <div>
                <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-[#0A0A0A]">New This Week</h2>
                <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono'] uppercase tracking-wider">Fresh handpicked classics</p>
              </div>
            </div>
            <Link
              to="/browse"
              className="hidden sm:flex items-center gap-1 text-sm font-bold text-[#FF3860] hover:underline font-['Nunito']"
              data-testid="view-all-new-link"
            >
              View All <ChevronRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 stagger-children">
            {newShows.map((show, i) => (
              <Link
                key={show.id}
                to={`/show/${show.id}`}
                className="neo-card overflow-hidden group bg-white relative animate-fade-in-up"
                style={{ opacity: 0 }}
                data-testid={`new-show-${show.id}`}
              >
                <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-md bg-[#FFDE00] border-2 border-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
                  <span className="font-['IBM_Plex_Mono'] text-[10px] font-bold text-[#0A0A0A] tracking-wider">NEW</span>
                </div>
                <div className="aspect-video border-b-2 border-[#0A0A0A] overflow-hidden bg-gray-100">
                  <img
                    src={show.thumbnail_url}
                    alt={show.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-3">
                  <h3 className="font-['Fredoka'] font-bold text-sm text-[#0A0A0A] truncate group-hover:text-[#FF3860] transition-colors">{show.title}</h3>
                  <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">{show.episode_count} {show.episode_count === 1 ? 'ep' : 'eps'}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Gentle donate nudge */}
          <Link
            to="/donate"
            className="mt-6 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#FF3860]/40 hover:border-[#FF3860] hover:bg-[#FF3860]/5 transition-all group"
            data-testid="new-shelf-donate-nudge"
          >
            <Coffee size={16} strokeWidth={2.5} className="text-[#FF3860]" />
            <span className="text-sm font-['Nunito'] font-medium text-[#4B5563] group-hover:text-[#FF3860] transition-colors">
              Enjoying the fresh picks? Buy me a coffee to keep the curation going
            </span>
          </Link>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-16" data-testid="categories-section">
        <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-8">Browse by Category</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {categories.map((cat) => (
            <Link
              key={cat.key}
              to={`/browse?category=${cat.key}`}
              className="neo-card flex items-center gap-5 p-6"
              style={{ backgroundColor: cat.color + '15' }}
              data-testid={`category-${cat.key}`}
            >
              <div
                className="w-14 h-14 rounded-xl border-2 border-[#0A0A0A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]"
                style={{ backgroundColor: cat.color }}
              >
                <cat.icon size={24} strokeWidth={2.5} color={cat.key === 'cartoon' ? 'white' : '#0A0A0A'} />
              </div>
              <div className="flex-1">
                <h3 className="font-['Fredoka'] font-bold text-xl text-[#0A0A0A]">{cat.label}</h3>
                <p className="text-sm text-[#4B5563] font-['Nunito']">{cat.desc}</p>
              </div>
              <ChevronRight size={20} strokeWidth={2.5} className="text-[#4B5563]" />
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Shows */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pb-16" data-testid="featured-section">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Featured Shows</h2>
          <Link
            to="/browse"
            className="flex items-center gap-1 text-sm font-bold text-[#FF3860] hover:underline font-['Nunito']"
            data-testid="view-all-link"
          >
            View All <ChevronRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="neo-card animate-pulse">
                <div className="aspect-video bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {featured.map((show, i) => (
              <div key={show.id} className="animate-fade-in-up" style={{ opacity: 0 }}>
                <ShowCard show={show} index={i} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Continue Watching */}
      {continueWatching.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pb-16" data-testid="continue-watching-section">
          <div className="flex items-center gap-2 mb-6">
            <Clock size={22} strokeWidth={2.5} className="text-[#FF3860]" />
            <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Continue Watching</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {continueWatching.slice(0, 4).map((item) => (
              <Link
                key={item.episode.id}
                to={`/watch/${item.episode.id}`}
                className="neo-card overflow-hidden group bg-white"
                data-testid={`continue-${item.episode.id}`}
              >
                <div className="relative aspect-video border-b-2 border-[#0A0A0A] overflow-hidden">
                  <img
                    src={item.episode.thumbnail_url || item.show.thumbnail_url || ''}
                    alt={item.episode.title}
                    className="w-full h-full object-cover vhs-thumbnail"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full bg-white/90 border-2 border-[#0A0A0A] flex items-center justify-center">
                      <Play size={16} fill="#FF3860" className="text-[#FF3860] ml-0.5" />
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
                    <div className="h-full bg-[#FF3860]" style={{ width: `${item.progress}%` }} />
                  </div>
                </div>
                <div className="p-3">
                  <p className="font-['Fredoka'] font-bold text-sm text-[#0A0A0A] truncate">{item.episode.title}</p>
                  <p className="text-xs text-[#4B5563] font-['Nunito'] truncate">{item.show.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Themed Playlists */}
      {playlists.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pb-16" data-testid="playlists-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <ListMusic size={22} strokeWidth={2.5} className="text-[#4AC6FF]" />
              <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Themed Playlists</h2>
            </div>
            <Link to="/playlists" className="flex items-center gap-1 text-sm font-bold text-[#FF3860] hover:underline font-['Nunito']" data-testid="view-all-playlists">
              View All <ChevronRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {playlists.slice(0, 3).map((pl, i) => (
              <Link
                key={pl.id}
                to={`/playlists/${pl.slug}`}
                className="neo-card flex items-center gap-4 p-4 group"
                style={{ backgroundColor: ['#FFF0F3', '#E8F4FD', '#FDFBEC'][i % 3] }}
                data-testid={`home-playlist-${pl.slug}`}
              >
                <div className="w-16 h-16 shrink-0 rounded-lg border-2 border-[#0A0A0A] overflow-hidden shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
                  <img src={pl.image_url} alt={pl.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] truncate group-hover:text-[#FF3860] transition-colors">{pl.title}</h3>
                  <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">{pl.show_count} shows</p>
                </div>
                <ChevronRight size={16} strokeWidth={2.5} className="text-[#4B5563] shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TV Mode CTA */}
      <section className="bg-[#0A0A0A] py-16" data-testid="tv-mode-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 text-center">
          <Zap size={40} strokeWidth={2.5} className="text-[#FFDE00] mx-auto mb-4" />
          <h2 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-white mb-4">
            Channel Surf Mode
          </h2>
            <p className="text-base text-white/70 font-['Nunito'] mb-8 max-w-md mx-auto">
            Just like TV nights of old! Random shows with retro commercial breaks. Sit back and let the TV decide.
          </p>
          <Link
            to="/tv-schedule"
            className="neo-btn inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-[#FFDE00] text-[#0A0A0A] text-base border-[#FFDE00]"
            data-testid="cta-tv-mode-btn"
          >
            <Zap size={18} strokeWidth={2.5} /> Tune In
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FDFBEC] border-t-2 border-[#0A0A0A] py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/ebea1ea5-9731-4354-90db-f3840d8f2692/images/80514a52e5403549c758b4eb7a39bf04c9d4fbd1f3d392449159024de0890e38.png"
              alt="Retro Playground TV"
              className="w-7 h-7"
            />
            <span className="font-['Fredoka'] font-bold text-lg text-[#0A0A0A]">Retro Playground TV</span>
          </div>
          <p className="text-sm text-[#4B5563] font-['Nunito'] mb-1">
            A handpicked collection of classic kids TV, available via YouTube. 
          </p>
          <p className="text-xs text-[#4B5563]/60 font-['Nunito'] mb-3">
            We do not host or own any video content. All rights belong to their respective owners.
          </p>
          <Link to="/donate" className="inline-flex items-center gap-1 text-sm font-bold text-[#FF3860] hover:underline font-['Nunito']" data-testid="footer-donate">
            <Heart size={14} strokeWidth={2.5} fill="#FF3860" /> Support Retro Playground TV
          </Link>
          <div className="flex items-center justify-center gap-4 mt-3">
            <Link to="/terms" className="text-xs text-[#4B5563]/60 hover:text-[#4B5563] font-['Nunito']">Terms & Privacy</Link>
            <Link to="/faq" className="text-xs text-[#4B5563]/60 hover:text-[#4B5563] font-['Nunito']">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
